import React, { useEffect, useMemo, useState } from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BlockIcon from "@mui/icons-material/Block";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import RemoveShoppingCartIcon from "@mui/icons-material/RemoveShoppingCart";
import ReactCountryFlag from "react-country-flag";
import AdminGridBoardService from "../AdminGridBoardService";
import DictionaryApiService from "../../../system/integrate/DictionaryApiService";
import Logger from "../../../../services/Logger";
import { getFillTabIndex, setFillTabIndex, removeFillTabEntry, FILL_TAB_KEY_GRID } from "../../../pzl_common/util/fillTabStorage";
import { formatMeaning } from "../../../pzl_common/FillDialogMeaning";
import { fillDialogCloseIconButtonSx, fillDialogTitleSx } from "../../../pzl_common/fillDialogSafeArea";
import GridFillNotInUsePanel from "./GridFillNotInUsePanel";

function getPositionsSet(item) {
    const pos = item?.coordinates?.positions ?? item?.positions ?? [];
    return new Set((Array.isArray(pos) ? pos : []).map((p) => `${p.row}-${p.col}`));
}

function positionsOverlapWithCart(item, cartItems) {
    if (!item || !Array.isArray(cartItems) || cartItems.length === 0) return false;
    const setA = getPositionsSet(item);
    if (setA.size === 0) return false;
    for (const c of cartItems) {
        const setB = getPositionsSet(c);
        for (const key of setA) if (setB.has(key)) return true;
    }
    return false;
}

function buildFilledCellKeys(filledCoordinates) {
    const set = new Set();
    if (!Array.isArray(filledCoordinates)) return set;
    filledCoordinates.forEach((entry) => {
        const posList = entry?.positions ?? entry?.coordinates?.positions ?? [];
        (Array.isArray(posList) ? posList : []).forEach((p) => set.add(`${p.row}-${p.col}`));
    });
    return set;
}

function countCellsWillFill(positions, filledCellKeys) {
    if (!Array.isArray(positions)) return 0;
    if (!filledCellKeys || filledCellKeys.size === 0) return positions.length;
    return positions.filter((p) => !filledCellKeys.has(`${p.row}-${p.col}`)).length;
}

function ResultCard({ item, koreanTranslation, koreanError, koreanLoading, onKoreanTranslate, onAddBannedWord, formatMeaningFn, onAddToCart, isInCart, filledCellKeys }) {
    const positions = item ? (item.coordinates?.positions ?? item.positions ?? []) : [];
    const cellsWillFill = countCellsWillFill(positions, filledCellKeys);
    return (
        <Box sx={{ py: { xs: 0.5, sm: 1 } }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: { xs: 1, sm: 2 } }}>
                <Typography variant="h6" fontWeight={600} sx={{ flexShrink: 0 }}>
                    {item?.word ?? ""}
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0, justifyContent: "center" }}>
                    {typeof item?.info?.score === "number" && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                            score: {item.info.score.toLocaleString()}
                        </Typography>
                    )}
                    {positions.length > 0 && (
                        <Typography variant="caption" color="text.secondary">
                            Positions: {positions.map((p) => `(${p.row},${p.col})`).join(", ")}, <Box component="span" sx={{ fontWeight: 700, color: "success.main" }}>{cellsWillFill} cells will fill</Box>
                        </Typography>
                    )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }} />
                {typeof onAddBannedWord === "function" && (item?.word ?? "").trim() && (
                    <Tooltip title="Add as banned word">
                        <IconButton
                            size="small"
                            onClick={() => onAddBannedWord((item?.word ?? "").trim())}
                            sx={{ p: 0.25, flexShrink: 0, color: "error.main" }}
                            aria-label="Add as banned word"
                        >
                            <BlockIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                {/* Korean flag icon - for future use
                {typeof onKoreanTranslate === "function" && (
                    <Tooltip title="Translate to Korean">
                        <IconButton
                            size="small"
                            onClick={onKoreanTranslate}
                            disabled={koreanLoading || !!item?.meaningsI18n?.korean}
                            sx={{ p: 0.25, flexShrink: 0 }}
                            aria-label="Translate to Korean"
                        >
                            {koreanLoading ? <CircularProgress size={20} /> : <ReactCountryFlag countryCode="KR" svg style={{ width: "1.25em", height: "1.25em" }} />}
                        </IconButton>
                    </Tooltip>
                )}
                */}
                {typeof onAddToCart === "function" && item && (item.coordinates?.positions ?? item.positions ?? []).length > 0 && cellsWillFill > 0 && (
                    <Tooltip title={isInCart ? "In cart" : "Add to cart (place multiple at once)"}>
                        <span>
                            <IconButton
                                size="small"
                                onClick={() => !isInCart && onAddToCart(item)}
                                disabled={isInCart}
                                sx={{ p: 0.25, flexShrink: 0, color: isInCart ? "action.disabled" : "primary.main" }}
                                aria-label={isInCart ? "In cart" : "Add to cart"}
                            >
                                <AddShoppingCartIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
            </Box>
            <Box sx={{ height: "40vh", overflow: "auto", mb: { xs: 1, sm: 2 }, flexShrink: 0 }}>
                <Box sx={{ mb: { xs: 1, sm: 2 } }}>{item?.meaning ? formatMeaningFn(item.meaning) : null}</Box>
                {koreanError && (
                    <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                        {koreanError}
                    </Typography>
                )}
                {item?.meaningsI18n && Object.entries(item.meaningsI18n).map(([lang, meaningObj]) => (
                    <Box key={lang} sx={{ mt: { xs: 1, sm: 2 }, p: { xs: 1, sm: 1.5 }, borderRadius: 1, bgcolor: "action.hover", border: "1px solid", borderColor: "divider" }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "primary.main", mb: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
                            {/* {lang === "korean" && <ReactCountryFlag countryCode="KR" svg style={{ width: "1em", height: "1em" }} />} */}
                            {lang.charAt(0).toUpperCase() + lang.slice(1)}
                        </Typography>
                        {formatMeaningFn(meaningObj)}
                    </Box>
                ))}
                {!item?.meaningsI18n?.korean && koreanTranslation?.meaningsI18n?.korean && (
                    <Box sx={{ mt: { xs: 1, sm: 2 }, p: { xs: 1, sm: 1.5 }, borderRadius: 1, bgcolor: "action.hover", border: "1px solid", borderColor: "divider" }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "primary.main", mb: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
                            {/* <ReactCountryFlag countryCode="KR" svg style={{ width: "1em", height: "1em" }} /> */}
                            Korean
                        </Typography>
                        {formatMeaningFn(koreanTranslation.meaningsI18n.korean)}
                    </Box>
                )}
            </Box>
        </Box>
    );
}

function NavFooter({ words, currentIndex, onIndexChange, onRefresh, onPlaceWord, onPlaceWords, loading, adding, disabled, currentItem, cart = [], onClearCart }) {
    const total = words?.length ?? 0;
    const canGoPrev = total > 1 && currentIndex > 0;
    const canGoNext = total > 1 && currentIndex < total - 1;
    const cartCount = cart?.length ?? 0;
    const handlePlace = async () => {
        if (cartCount > 0) {
            if (typeof onPlaceWords === "function") {
                await onPlaceWords(cart);
                typeof onClearCart === "function" && onClearCart();
            } else if (typeof onPlaceWord === "function") {
                for (const item of cart) {
                    await onPlaceWord(item);
                }
                typeof onClearCart === "function" && onClearCart();
            }
        } else if (currentItem && typeof onPlaceWord === "function") {
            await onPlaceWord(currentItem);
        }
    };
    return (
        <Box sx={{ display: "flex", flexGrow: 1, justifyContent: "space-between", alignItems: "center", gap: { xs: 0.5, sm: 1 }, flexWrap: { xs: "nowrap", sm: "wrap" } }}>
            <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 0.5 }}>
                {(total > 0 || cartCount > 0) && (
                    <Button variant="contained" color="primary" size="small" onClick={handlePlace} disabled={adding} sx={{ minWidth: { xs: "auto", sm: 64 }, px: { xs: 1, sm: 2 } }}>
                        {adding ? "Adding…" : cartCount > 0 ? `Place (${cartCount})` : "Place"}
                    </Button>
                )}
                {cartCount > 0 && (
                    <Tooltip title="Clear cart">
                        <IconButton size="small" onClick={() => typeof onClearCart === "function" && onClearCart()} aria-label="Clear cart" sx={{ p: 0.5 }}>
                            <RemoveShoppingCartIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1 }, flexShrink: 0, marginLeft: "auto" }}>
                <IconButton size="small" onClick={() => onIndexChange((i) => Math.max(0, i - 1))} disabled={!canGoPrev} aria-label="Previous">
                    <ChevronLeftIcon />
                </IconButton>
                <Typography variant="body2" color="text.secondary" noWrap>
                    {total > 0 ? `${currentIndex + 1} / ${total}` : "0 / 0"}
                </Typography>
                <IconButton size="small" onClick={() => onIndexChange((i) => Math.min(total - 1, i + 1))} disabled={!canGoNext} aria-label="Next">
                    <ChevronRightIcon />
                </IconButton>
                <Button variant="outlined" size="small" onClick={onRefresh} disabled={disabled || loading} sx={{ minWidth: { xs: "auto", sm: 64 }, px: { xs: 1, sm: 2 } }}>
                    {loading ? "Finding…" : "Find"}
                </Button>
            </Box>
        </Box>
    );
}

/**
 * Fill common dialog: all UI and logic for tabs 1–3. Minimal props; owns all state and fetch/handlers.
 */
function formatPuzzleTypeLabel(type) {
    if (!type || typeof type !== "string") return type;
    const t = type.toUpperCase().replace(/-/g, "_");
    if (t === "WORD_SEARCH") return "Word Search";
    if (t === "HIDDEN_MESSAGE") return "Hidden Message";
    if (t === "CROSSWORD") return "Crossword";
    return type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function GridFillDialog({
    open,
    onClose,
    title = "Fill",
    subtitle,
    puzzleTitle,
    puzzleType,
    tags = [],
    gridId,
    language = "english",
    cellsFullyFilled = false,
    emptyCellCount,
    onPlaceWord,
    onPlaceWords,
    onTab2Add,
    onNotInUsePlaceWord,
    adding = false,
    wordsNotInUse = [],
    onReadyToReview,
    readyToReviewDisabled = false,
    readyToReviewLoading = false,
    onAddBannedWord,
    filledCoordinates = [],
    onShowSnackbar,
    onFillMoreSuccess
}) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const filledCellKeys = useMemo(() => buildFilledCellKeys(filledCoordinates), [filledCoordinates]);
    const [activeTab, setActiveTab] = useState(1);
    const [cart, setCart] = useState([]);
    const [notInUsePanelOpen, setNotInUsePanelOpen] = useState(false);
    const [notInUseSelectedWord, setNotInUseSelectedWord] = useState(null);
    const [filterNotInUseByLength, setFilterNotInUseByLength] = useState(null);

    // Restore tab from storage when dialog opens; remove entry when puzzle has no empty cells
    useEffect(() => {
        if (!open || !gridId) return;
        if (cellsFullyFilled || emptyCellCount === 0) {
            removeFillTabEntry(FILL_TAB_KEY_GRID, gridId);
            return;
        }
        const stored = getFillTabIndex(FILL_TAB_KEY_GRID, gridId);
        if (typeof stored === "number" && stored >= 1 && stored <= 3) {
            setActiveTab(stored);
        }
    }, [open, gridId, cellsFullyFilled, emptyCellCount]);

    // Reset words-not-in-use panel state when dialog closes
    useEffect(() => {
        if (!open) {
            setNotInUsePanelOpen(false);
            setNotInUseSelectedWord(null);
            setFilterNotInUseByLength(null);
        }
    }, [open]);

    // Tab 1
    const [tab1Result, setTab1Result] = useState(null);
    const [tab1Error, setTab1Error] = useState(null);
    const [loadingTab1, setLoadingTab1] = useState(false);
    const [tab1CurrentIndex, setTab1CurrentIndex] = useState(0);

    // Tab 2 (find less-related words – uses tab3Result)
    const [tab3Result, setTab3Result] = useState(null);
    const [tab3Error, setTab3Error] = useState(null);
    const [loadingTab3, setLoadingTab3] = useState(false);
    const [tab3CurrentIndex, setTab3CurrentIndex] = useState(0);
    const [tab3KoreanTranslation, setTab3KoreanTranslation] = useState(null);
    const [tab3KoreanLoading, setTab3KoreanLoading] = useState(false);
    const [tab3KoreanError, setTab3KoreanError] = useState(null);

    // Tab 3 (getFinal4Candidates – 4–6-letter words for ≤4 blanks)
    const [tab4Result, setTab4Result] = useState(null);
    const [tab4Error, setTab4Error] = useState(null);
    const [loadingTab4, setLoadingTab4] = useState(false);
    const [tab4CurrentIndex, setTab4CurrentIndex] = useState(0);
    const [tab4KoreanTranslation, setTab4KoreanTranslation] = useState(null);
    const [tab4KoreanLoading, setTab4KoreanLoading] = useState(false);
    const [tab4KoreanError, setTab4KoreanError] = useState(null);

    const words = (r) => r?.wordsWithPositions ?? [];
    const hasWords = (r) => (r?.wordsWithPositions?.length ?? 0) > 0;
    const isEmpty = (r, err) => r != null && !err && ((r.wordsWithPositions?.length === 0) || (r.wordsFound === 0));

    const filterByCart = (wordList) => (Array.isArray(wordList) ? wordList : []).filter((w) => !positionsOverlapWithCart(w, cart));

    const tab1Full = words(tab1Result);
    const tab3Full = words(tab3Result);
    const tab4Full = words(tab4Result);
    const tab1Filtered = filterByCart(tab1Full);
    const tab3Filtered = filterByCart(tab3Full);
    const tab4Filtered = filterByCart(tab4Full);

    // Clamp indices to filtered list length (filtered list shrinks when cart has overlapping items)
    useEffect(() => {
        setTab1CurrentIndex((i) => Math.min(i, Math.max(0, tab1Filtered.length - 1)));
    }, [tab1Filtered.length]);
    useEffect(() => {
        setTab3CurrentIndex((i) => Math.min(i, Math.max(0, tab3Filtered.length - 1)));
    }, [tab3Filtered.length]);
    useEffect(() => {
        setTab4CurrentIndex((i) => Math.min(i, Math.max(0, tab4Filtered.length - 1)));
    }, [tab4Filtered.length]);
    useEffect(() => {
        setTab3KoreanTranslation(null);
        setTab3KoreanError(null);
    }, [tab3CurrentIndex]);
    useEffect(() => {
        setTab4KoreanTranslation(null);
        setTab4KoreanError(null);
    }, [tab4CurrentIndex]);

    // Clear all state when dialog closes
    useEffect(() => {
        if (!open) {
            setCart([]);
            setTab1Result(null);
            setTab1Error(null);
            setTab1CurrentIndex(0);
            setTab3Result(null);
            setTab3Error(null);
            setTab3CurrentIndex(0);
            setTab3KoreanTranslation(null);
            setTab3KoreanError(null);
            setTab4Result(null);
            setTab4Error(null);
            setTab4CurrentIndex(0);
            setTab4KoreanTranslation(null);
            setTab4KoreanError(null);
        }
    }, [open]);

    const addToCart = (item) => {
        if (!item) return;
        const positions = item?.coordinates?.positions ?? item?.positions ?? [];
        if (positions.length === 0) return;
        if (countCellsWillFill(positions, filledCellKeys) === 0) return;
        setCart((prev) => {
            if (positionsOverlapWithCart(item, prev)) return prev;
            const key = (i) => `${i?.word ?? ""}-${JSON.stringify(i?.coordinates?.positions ?? i?.positions ?? [])}`;
            const k = key(item);
            if (prev.some((p) => key(p) === k)) return prev;
            // Store a snapshot so each cart item keeps its own positions (avoids shared API reference)
            const snap = {
                ...item,
                coordinates: item.coordinates
                    ? {
                          ...item.coordinates,
                          positions: (item.coordinates.positions || []).map((p) => ({ row: p.row, col: p.col }))
                      }
                    : undefined,
                positions: (item.positions || []).map((p) => ({ row: p.row, col: p.col }))
            };
            return [...prev, snap];
        });
    };

    const isItemInCart = (item) => {
        if (!item || cart.length === 0) return false;
        const key = (i) => `${i?.word ?? ""}-${JSON.stringify(i?.coordinates?.positions ?? i?.positions ?? [])}`;
        const k = key(item);
        return cart.some((c) => key(c) === k);
    };

    const handleTabChange = (e, value) => {
        if (value != null) {
            setActiveTab(value);
            setNotInUsePanelOpen(false);
            if (gridId) setFillTabIndex(FILL_TAB_KEY_GRID, gridId, value);
        }
    };

    const handleTab1Fetch = async () => {
        if (!gridId) return;
        setCart([]);
        setLoadingTab1(true);
        setTab1Error(null);
        try {
            const result = await AdminGridBoardService.getFillAssocBedrock(gridId);
            setTab1Result(result);
        } catch (err) {
            Logger.error("Error fetching fill assoc:", err);
            setTab1Error(err?.message || "Fill assoc failed.");
            setTab1Result(null);
        } finally {
            setLoadingTab1(false);
        }
    };
    const handleTab1PlaceWord = async (item) => {
        if (onPlaceWord) await onPlaceWord(item);
        setTab1Result(null);
    };

    const handleTab3Fetch = async () => {
        if (!gridId) return;
        setCart([]);
        setLoadingTab3(true);
        setTab3Error(null);
        try {
            const result = await AdminGridBoardService.getFillRestBedrock(gridId);
            setTab3Result(result);
        } catch (err) {
            Logger.error("Error filling rest:", err);
            setTab3Error(err?.message || "Fill rest failed.");
            setTab3Result(null);
        } finally {
            setLoadingTab3(false);
        }
    };
    const handleTab3PlaceWord = async (item) => {
        if (onPlaceWord) await onPlaceWord(item);
        setTab3Result(null);
    };
    const handleTab3KoreanTranslate = async () => {
        const w = words(tab3Result);
        const item = w[tab3CurrentIndex];
        const word = item?.word?.trim();
        if (!word) return;
        setTab3KoreanLoading(true);
        setTab3KoreanError(null);
        setTab3KoreanTranslation(null);
        try {
            const res = await DictionaryApiService.translateWord(word, language, "korean");
            setTab3KoreanTranslation(res);
        } catch (err) {
            Logger.error("Error fetching Korean translation:", err);
            setTab3KoreanError(err?.message || "Failed to fetch translation");
        } finally {
            setTab3KoreanLoading(false);
        }
    };

    const handleTab4Fetch = async () => {
        if (!gridId) return;
        setCart([]);
        setLoadingTab4(true);
        setTab4Error(null);
        try {
            const result = await AdminGridBoardService.getFinal4Candidates(gridId);
            setTab4Result(result);
        } catch (err) {
            Logger.error("Error fetching Final 4:", err);
            setTab4Error(err?.message || "Final 4 failed.");
            setTab4Result(null);
        } finally {
            setLoadingTab4(false);
        }
    };
    const handleTab4PlaceWord = async (item) => {
        if (onPlaceWord) await onPlaceWord(item);
        setTab4Result(null);
    };
    const handleTab4KoreanTranslate = async () => {
        const w = words(tab4Result);
        const item = w[tab4CurrentIndex];
        const word = item?.word?.trim();
        if (!word) return;
        setTab4KoreanLoading(true);
        setTab4KoreanError(null);
        setTab4KoreanTranslation(null);
        try {
            const res = await DictionaryApiService.translateWord(word, language, "korean");
            setTab4KoreanTranslation(res);
        } catch (err) {
            Logger.error("Error fetching Korean translation:", err);
            setTab4KoreanError(err?.message || "Failed to fetch translation");
        } finally {
            setTab4KoreanLoading(false);
        }
    };

    const renderFooter = () => {
        if (activeTab === 1 && gridId) {
            return (
                <NavFooter
                    words={tab1Filtered}
                    currentIndex={tab1CurrentIndex}
                    onIndexChange={setTab1CurrentIndex}
                    onRefresh={handleTab1Fetch}
                    onPlaceWord={handleTab1PlaceWord}
                    onPlaceWords={onPlaceWords}
                    loading={loadingTab1}
                    adding={adding}
                    disabled={cellsFullyFilled}
                    currentItem={tab1Filtered[tab1CurrentIndex]}
                    cart={cart}
                    onClearCart={() => setCart([])}
                />
            );
        }
        if (activeTab === 2 && gridId) {
            return (
                <NavFooter
                    words={tab3Filtered}
                    currentIndex={tab3CurrentIndex}
                    onIndexChange={setTab3CurrentIndex}
                    onRefresh={handleTab3Fetch}
                    onPlaceWord={handleTab3PlaceWord}
                    onPlaceWords={onPlaceWords}
                    loading={loadingTab3}
                    adding={adding}
                    disabled={cellsFullyFilled}
                    currentItem={tab3Filtered[tab3CurrentIndex]}
                    cart={cart}
                    onClearCart={() => setCart([])}
                />
            );
        }
        if (activeTab === 3 && gridId) {
            return (
                <NavFooter
                    words={tab4Filtered}
                    currentIndex={tab4CurrentIndex}
                    onIndexChange={setTab4CurrentIndex}
                    onRefresh={handleTab4Fetch}
                    onPlaceWord={handleTab4PlaceWord}
                    onPlaceWords={onPlaceWords}
                    loading={loadingTab4}
                    adding={adding}
                    disabled={cellsFullyFilled}
                    currentItem={tab4Filtered[tab4CurrentIndex]}
                    cart={cart}
                    onClearCart={() => setCart([])}
                />
            );
        }
        return null;
    };

    const isHiddenMessage = puzzleType === "HIDDEN_MESSAGE";
    const isFrenchPuzzle = String(language ?? "").toLowerCase() === "french";

    const renderAlertWithNotInUse = (alertContent) => (
        <>
            <Alert severity="info" icon={false} sx={{ mb: { xs: 1, sm: 2 }, "& .MuiAlert-message": { display: "flex", alignItems: "center", gap: 0.5 } }}>{alertContent}</Alert>
            {notInUsePanelOpen && isHiddenMessage && (
                <GridFillNotInUsePanel
                    isFrenchPuzzle={isFrenchPuzzle}
                    wordsNotInUse={wordsNotInUse}
                    selectedWord={notInUseSelectedWord}
                    onSelectWord={setNotInUseSelectedWord}
                    onPlaceWord={onNotInUsePlaceWord}
                    onAddAllWords={gridId && onShowSnackbar && onFillMoreSuccess ? async () => {
                        try {
                            const result = await AdminGridBoardService.fillMore(gridId);
                            onFillMoreSuccess();
                            const wordsPlaced = result?.wordsPlaced ?? result?.addedCount ?? result?.wordsAdded ?? 0;
                            if (wordsPlaced > 0) {
                                onShowSnackbar(`${wordsPlaced} word(s) added to puzzle`, "success");
                            } else {
                                onShowSnackbar(result?.message ?? "No additional words placed", "info");
                            }
                        } catch (err) {
                            Logger.error("Error fill-more:", err);
                            onShowSnackbar("Error adding words: " + (err?.message || err), "error");
                        }
                    } : undefined}
                    filterByLength={filterNotInUseByLength}
                    gridId={gridId}
                    emptyCellCount={emptyCellCount}
                    onShowSnackbar={onShowSnackbar}
                />
            )}
        </>
    );

    const renderContent = () => {
        if (activeTab === 1) {
            const tab1Item = tab1Filtered[tab1CurrentIndex];
            return (
                <>
                    {renderAlertWithNotInUse(
                        <>
                            <Tooltip title="Bedrock AI">
                                <span style={{ display: "inline-flex" }}>
                                    <AutoAwesomeIcon sx={{ fontSize: 18, verticalAlign: "middle", mr: 0.5 }} />
                                </span>
                            </Tooltip>
                            Find associated words
                        </>
                    )}
                    {tab1Error && <Typography color="error" variant="body2" sx={{ mb: 1 }}>{tab1Error}</Typography>}
                    {tab1Result != null && !tab1Error && isEmpty(tab1Result, tab1Error) && (
                        <Typography color="text.secondary" variant="body2">No words with positions returned.</Typography>
                    )}
                    {tab1Result == null && !tab1Error && (
                        <Typography variant="body2" color="text.secondary">{loadingTab1 ? "Finding…" : "Result list will appear here"}</Typography>
                    )}
                    {tab1Result != null && !tab1Error && hasWords(tab1Result) && (
                        <ResultCard
                            item={tab1Item}
                            onAddBannedWord={onAddBannedWord}
                            formatMeaningFn={formatMeaning}
                            onAddToCart={addToCart}
                            isInCart={isItemInCart(tab1Item)}
                            filledCellKeys={filledCellKeys}
                        />
                    )}
                </>
            );
        }
        if (activeTab === 2) {
            const item = tab3Filtered[tab3CurrentIndex];
            return (
                <>
                    {renderAlertWithNotInUse(
                        <>
                            <Tooltip title="Bedrock AI">
                                <span style={{ display: "inline-flex" }}>
                                    <AutoAwesomeIcon sx={{ fontSize: 18, verticalAlign: "middle", mr: 0.5 }} />
                                </span>
                            </Tooltip>
                            Find less-related 4–6-letter words
                        </>
                    )}
                    {tab3Error && <Typography color="error" variant="body2" sx={{ mb: 1 }}>{tab3Error}</Typography>}
                    {tab3Result != null && !tab3Error && isEmpty(tab3Result, tab3Error) && (
                        <Typography color="text.secondary" variant="body2">No words with positions returned.</Typography>
                    )}
                    {tab3Result == null && !tab3Error && (
                        <Typography variant="body2" color="text.secondary">{loadingTab3 ? "Finding…" : "Result list will appear here"}</Typography>
                    )}
                    {tab3Result != null && !tab3Error && hasWords(tab3Result) && (
                        <ResultCard
                            item={item}
                            koreanTranslation={tab3KoreanTranslation}
                            koreanError={tab3KoreanError}
                            koreanLoading={tab3KoreanLoading}
                            onKoreanTranslate={handleTab3KoreanTranslate}
                            onAddBannedWord={onAddBannedWord}
                            formatMeaningFn={formatMeaning}
                            onAddToCart={addToCart}
                            isInCart={isItemInCart(item)}
                            filledCellKeys={filledCellKeys}
                        />
                    )}
                </>
            );
        }
        if (activeTab === 3) {
            const item = tab4Filtered[tab4CurrentIndex];
            return (
                <>
                    {renderAlertWithNotInUse(
                        <>
                            <MenuBookIcon sx={{ fontSize: 18, verticalAlign: "middle", mr: 0.5 }} />
                            4–6-letter words for ≤4 blanks
                        </>
                    )}
                    {tab4Error && <Typography color="error" variant="body2" sx={{ mb: 1 }}>{tab4Error}</Typography>}
                    {tab4Result != null && !tab4Error && isEmpty(tab4Result, tab4Error) && (
                        <Typography color="text.secondary" variant="body2">No words with positions returned.</Typography>
                    )}
                    {tab4Result == null && !tab4Error && (
                        <Typography variant="body2" color="text.secondary">{loadingTab4 ? "Finding…" : "Result list will appear here"}</Typography>
                    )}
                    {tab4Result != null && !tab4Error && hasWords(tab4Result) && (
                        <ResultCard
                            item={item}
                            koreanTranslation={tab4KoreanTranslation}
                            koreanError={tab4KoreanError}
                            koreanLoading={tab4KoreanLoading}
                            onKoreanTranslate={handleTab4KoreanTranslate}
                            onAddBannedWord={onAddBannedWord}
                            formatMeaningFn={formatMeaning}
                            onAddToCart={addToCart}
                            isInCart={isItemInCart(item)}
                            filledCellKeys={filledCellKeys}
                        />
                    )}
                </>
            );
        }
        return null;
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen={fullScreen}
            maxWidth="md"
            fullWidth
            aria-labelledby="fill-common-dialog-title"
            sx={{
                "& .MuiDialog-container": {
                    paddingTop: 0,
                    paddingBottom: 0,
                    paddingLeft: { xs: 0, sm: "8px" },
                    paddingRight: { xs: 0, sm: "8px" },
                    alignItems: { xs: "stretch", sm: "center" }
                }
            }}
            PaperProps={{
                sx: {
                    margin: 0,
                    maxHeight: { xs: "100vh", sm: "100vh" }
                }
            }}
        >
            <DialogTitle id="fill-common-dialog-title" sx={fillDialogTitleSx}>
                <IconButton
                    aria-label="Close"
                    onClick={onClose}
                    sx={fillDialogCloseIconButtonSx}
                >
                    <CloseIcon />
                </IconButton>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    {puzzleType && (
                        <Chip
                            label={formatPuzzleTypeLabel(puzzleType)}
                            size="small"
                            variant="outlined"
                            sx={{ height: 24, fontSize: "0.75rem" }}
                        />
                    )}
                    {puzzleTitle && (
                        <Typography
                            variant="h6"
                            color="primary"
                            sx={{ fontWeight: "bold", display: "block" }}
                        >
                            {puzzleTitle}
                        </Typography>
                    )}
                </Box>
                {Array.isArray(tags) && tags.length > 0 && (
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: { xs: 0.5, sm: 1 } }}>
                        {tags
                            .filter((tag) => {
                                const label = typeof tag === "string" ? tag : tag?.name || tag?.label || String(tag);
                                return String(label).toLowerCase() !== "bedrock";
                            })
                            .map((tag, i) => (
                            <Chip
                                key={i}
                                label={typeof tag === "string" ? tag : tag?.name || tag?.label || String(tag)}
                                size="small"
                                variant="outlined"
                                sx={{ height: 24, fontSize: "0.75rem" }}
                            />
                            ))}
                    </Box>
                )}
            </DialogTitle>
            <DialogContent dividers sx={{ minHeight: "50vh", px: { xs: 0.5, sm: 2 }, py: { xs: 1, sm: 2 } }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1 }, flexWrap: { xs: "nowrap", sm: "wrap" }, mb: { xs: 1, sm: 2 } }}>
                    {emptyCellCount != null ? (
                        emptyCellCount === 0 ? (
                            <Box
                                sx={{
                                    px: { xs: 1, sm: 1.5 },
                                    py: 0.5,
                                    border: "1px solid",
                                    borderColor: "#2e7d32",
                                    borderRadius: 1,
                                    bgcolor: "rgba(46, 125, 50, 0.12)",
                                    flexShrink: 0
                                }}
                            >
                                <Typography variant="body2" sx={{ color: "#2e7d32", fontWeight: 600, fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                                    Full Filled
                                </Typography>
                            </Box>
                        ) : (
                            <Tooltip
                                title={
                                    puzzleType === "HIDDEN_MESSAGE"
                                        ? filterNotInUseByLength === emptyCellCount
                                            ? "Show all words (click to clear filter)"
                                            : "Filter words not in use by this length"
                                        : ""
                                }
                            >
                                <Box
                                    onClick={() => puzzleType === "HIDDEN_MESSAGE" && setFilterNotInUseByLength((prev) => (prev === emptyCellCount ? null : emptyCellCount))}
                                    sx={{
                                        px: { xs: 1, sm: 1.5 },
                                        py: 0.5,
                                        border: "1px solid",
                                        borderColor: filterNotInUseByLength === emptyCellCount ? "primary.main" : "#d32f2f",
                                        borderRadius: 1,
                                        bgcolor: filterNotInUseByLength === emptyCellCount ? "rgba(25, 118, 210, 0.12)" : "rgba(211, 47, 47, 0.08)",
                                        flexShrink: 0,
                                        ...(puzzleType === "HIDDEN_MESSAGE" && { cursor: "pointer" })
                                    }}
                                >
                                    <Typography variant="body2" sx={{ color: filterNotInUseByLength === emptyCellCount ? "primary.main" : "#d32f2f", fontWeight: 600, fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                                        Empty: {Number(emptyCellCount).toLocaleString()}
                                    </Typography>
                                </Box>
                            </Tooltip>
                        )
                    ) : null}
                    <Box sx={{ flex: 1, minWidth: 0, flexShrink: 1 }} />
                    <ToggleButtonGroup
                        value={activeTab}
                        exclusive
                        onChange={handleTabChange}
                        size="small"
                        sx={{
                            flexShrink: 0,
                            height: 24,
                            "& .MuiToggleButton-root": { height: 24, minWidth: 32, py: 0, fontSize: "0.75rem" }
                        }}
                    >
                        <ToggleButton value={1}>1</ToggleButton>
                        <ToggleButton value={2}>2</ToggleButton>
                        <ToggleButton value={3}>3</ToggleButton>
                    </ToggleButtonGroup>
                    {isHiddenMessage && (
                        <Tooltip title={notInUsePanelOpen ? "Hide words not in use" : "Show words not in use"}>
                            <IconButton
                                size="small"
                                onClick={() => setNotInUsePanelOpen((o) => !o)}
                                aria-label={notInUsePanelOpen ? "Hide words not in use" : "Show words not in use"}
                                sx={{ height: 24, width: 32, minWidth: 32, p: 0, flexShrink: 0 }}
                            >
                                {notInUsePanelOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
                <Box sx={{ minHeight: "40vh" }}>
                    {renderContent()}
                </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: "flex-start", flexWrap: "wrap", gap: 1, pl: { xs: 0.5, sm: 2 }, pr: { xs: 0.5, sm: 1 }, py: { xs: 1, sm: 2 } }}>
                {cellsFullyFilled && typeof onReadyToReview === "function" && (
                    <Button
                        variant="outlined"
                        color="success"
                        size="small"
                        startIcon={readyToReviewLoading ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                        onClick={onReadyToReview}
                        disabled={readyToReviewDisabled || readyToReviewLoading}
                        sx={{ minWidth: 120, whiteSpace: "nowrap" }}
                    >
                        {readyToReviewLoading ? "Sending…" : "Ready to Review"}
                    </Button>
                )}
                {renderFooter()}
            </DialogActions>
        </Dialog>
    );
}
